import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Quiz from "@/components/Quiz";
import {
  Minus,
  Plus,
  CheckCircle2,
  XCircle,
  RotateCcw,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { t } from "@/i18n";

const PERSONS = [
  { id: "1sg", l: "1ʳᵉ sg (je)" },
  { id: "2ms", l: "2ᵉ masc sg" },
  { id: "2fs", l: "2ᵉ fém sg" },
  { id: "3ms", l: "3ᵉ masc sg" },
  { id: "3fs", l: "3ᵉ fém sg" },
  { id: "1pl", l: "1ʳᵉ pl (nous)" },
  { id: "3mp", l: "3ᵉ masc pl" },
];
const TENSES = ["Passé", "Présent", "Impératif"];
const ROOTS = ["Toutes", "Saine", "Creuse", "Défectueuse", "Hamzée"];

export default function PracticeConjugation() {
  const navigate = useNavigate();
  const [count, setCount] = useState(10);
  const [tense, setTense] = useState("Passé");
  const [rootType, setRootType] = useState("Toutes");
  const [persons, setPersons] = useState(["1sg", "3ms", "3fs"]);
  const [avail, setAvail] = useState(0);
  const [session, setSession] = useState(null);
  const [done, setDone] = useState(null);

  const load = async () => {
    const { data } = await api.get(
      `/practice/conjugation?count=1&tense=${tense}&root_type=${rootType}&persons=${persons.join(",")}`,
    );
    setAvail(data.available);
  };
  useEffect(() => {
    load();
  }, [tense, rootType, persons]);

  const togglePerson = (id) =>
    setPersons((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );

  const preset = (kind) => {
    if (kind === "beginner") {
      setTense("Passé");
      setRootType("Saine");
      setPersons(["1sg", "2ms", "3ms", "3fs"]);
    } else {
      setTense("Présent");
      setRootType("Toutes");
      setPersons(PERSONS.map((p) => p.id));
    }
  };

  const start = async () => {
    const { data } = await api.get(
      `/practice/conjugation?count=${count}&tense=${tense}&root_type=${rootType}&persons=${persons.join(",")}`,
    );
    setSession(data.questions.map((q) => ({ ...q, skill: "grammar" })));
    setDone(null);
  };

  // — Quiz in progress —
  if (session && !done) {
    return (
      <div className="max-w-xl mx-auto">
        <h1 className="font-head text-2xl font-bold mb-4">{t("practiceconjugation.conjugaison")}</h1>
        <Quiz questions={session} onDone={(r) => setDone(r)} arabicOptions rewardMode="normal" rewardAmount={1} />
      </div>
    );
  }

  // — Results screen —
  if (done) {
    const score = Math.round((done.correct / done.total) * 100);
    const passed = score >= 70;
    return (
      <div className="max-w-xl mx-auto text-center">
        <div
          className="rounded-2xl border bg-card p-8 mb-4"
          data-testid="conj-result"
        >
          {passed ? (
            <CheckCircle2
              size={52}
              className="mx-auto mb-3"
              style={{ color: "#16A34A" }}
            />
          ) : (
            <XCircle size={52} className="mx-auto mb-3 text-destructive" />
          )}
          <h2 className="font-head text-2xl font-bold mb-1">
            {t("practiceconjugation.session_terminee")}
          </h2>
          <p className="text-muted-foreground mb-4">
            {passed
              ? t("practiceconjugation.bien_joue_continue_comme_ca")
              : t("practiceconjugation.continue_a_pratiquer_tu_vas_y")}
          </p>
          {/* Score bar */}
          <div className="mb-4">
            <div className="flex justify-between text-sm text-muted-foreground mb-1">
              <span>{done.correct} correctes</span>
              <span>{done.total - done.correct} {t("lessonpage.ratees")}</span>
            </div>
            <div className="h-3 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${score}%`,
                  backgroundColor: passed
                    ? "#16A34A"
                    : "hsl(var(--destructive))",
                }}
              />
            </div>
          </div>
          <div
            className="text-4xl font-head font-bold mb-1"
            style={{ color: passed ? "#16A34A" : undefined }}
          >
            {score}%
          </div>
          <div className="text-sm text-muted-foreground mb-6">
            {done.correct} / {done.total} {t("interexerciserecap.bonnes_reponses")}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* Restart with the same questions */}
            <button
              onClick={() => setDone(null)}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
            >
              <RotateCcw size={16} /> {t("practiceconjugation.memes_questions")}
            </button>
            {/* Back to config */}
            <button
              onClick={() => {
                setDone(null);
                setSession(null);
              }}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border font-medium hover:bg-secondary transition"
            >
              <RefreshCw size={16} /> {t("practiceconjugation.nouvelle_selection")}
            </button>
            <button
              onClick={() => navigate("/practice")}
              className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border font-medium hover:bg-secondary transition"
            >
              <ArrowLeft size={16} /> {t("alphabetmodules.retour")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // — Configuration screen —
  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-head text-3xl font-bold mb-1">
        {t("practiceconjugation.entrainement_de_conjugaison")}
      </h1>
      <p className="text-muted-foreground mb-4">
        {t("practiceconjugation.la_conjugaison_arabe_combine_beaucoup_de")}
      </p>

      <div className="flex gap-3 mb-6">
        <button
          data-testid="preset-beginner"
          onClick={() => preset("beginner")}
          className="px-4 py-2 rounded-xl border font-medium hover:bg-secondary transition"
        >
          {t("practiceconjugation.debutant")}
        </button>
        <button
          data-testid="preset-full"
          onClick={() => preset("full")}
          className="px-4 py-2 rounded-xl border font-medium hover:bg-secondary transition"
        >
          {t("practiceconjugation.complet")}
        </button>
        <div
          className="ml-auto text-sm text-muted-foreground self-center"
          data-testid="avail-count"
        >
          {avail} {t("practiceconjugation.questions_disponibles")}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border bg-card p-4">
          <div className="text-sm font-medium mb-2">{t("dailyplansession.temps")}</div>
          <div className="flex flex-wrap gap-2">
            {TENSES.map((t) => (
              <button
                key={t}
                data-testid={`tense-${t}`}
                onClick={() => setTense(t)}
                className={`px-3 py-1.5 rounded-lg text-sm border ${tense === t ? "bg-primary text-primary-foreground border-primary" : ""}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border bg-card p-4">
          <div className="text-sm font-medium mb-2">{t("practiceconjugation.type_de_racine")}</div>
          <select
            data-testid="root-type"
            value={rootType}
            onChange={(e) => setRootType(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border bg-card"
          >
            {ROOTS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4 mb-6">
        <div className="text-sm font-medium mb-2">{t("practiceconjugation.personnes_a_inclure")}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {PERSONS.map((p) => (
            <label
              key={p.id}
              data-testid={`person-${p.id}`}
              className="flex items-center gap-2 text-sm cursor-pointer"
            >
              <input
                type="checkbox"
                checked={persons.includes(p.id)}
                onChange={() => togglePerson(p.id)}
                className="accent-[#4F46E5]"
              />
              {p.l}
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border bg-card p-4 mb-6 flex items-center gap-3">
        <span className="text-sm font-medium">{t("coursequestions.nombre_de_questions")}</span>
        <button
          data-testid="conj-minus"
          onClick={() => setCount((c) => Math.max(5, c - 5))}
          className="w-9 h-9 rounded-lg border flex items-center justify-center"
        >
          <Minus size={16} />
        </button>
        <span className="font-head text-xl font-bold w-8 text-center">
          {count}
        </span>
        <button
          data-testid="conj-plus"
          onClick={() => setCount((c) => c + 5)}
          className="w-9 h-9 rounded-lg border flex items-center justify-center"
        >
          <Plus size={16} />
        </button>
      </div>

      <button
        data-testid="start-conj"
        disabled={persons.length === 0 || avail === 0}
        onClick={start}
        className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition disabled:opacity-40"
      >
        {t("practiceconjugation.demarrer_la_pratique")}
      </button>
    </div>
  );
}
